/**
 * Remember the company Control Bridge primary workspace when navigating into a
 * linked / engineering submodel so the UI can always offer a path back.
 */

export const CONTROL_BRIDGE_PRIMARY_WORKSPACE_KEY = 'controlBridgePrimaryWorkspaceSlug';

export function readControlBridgePrimaryWorkspace(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const v = sessionStorage.getItem(CONTROL_BRIDGE_PRIMARY_WORKSPACE_KEY)?.trim();
    return v || null;
  } catch {
    return null;
  }
}

export function writeControlBridgePrimaryWorkspace(slug: string | null | undefined): void {
  if (typeof sessionStorage === 'undefined') return;
  const next = slug?.trim() ?? '';
  try {
    if (!next) {
      sessionStorage.removeItem(CONTROL_BRIDGE_PRIMARY_WORKSPACE_KEY);
      return;
    }
    sessionStorage.setItem(CONTROL_BRIDGE_PRIMARY_WORKSPACE_KEY, next);
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * When leaving a workspace for another CB slug, remember `fromSlug` as primary
 * unless a preferred primary is already known and we're leaving that preferred.
 */
export function rememberPrimaryWhenLeavingWorkspace(input: {
  fromSlug: string;
  toSlug: string;
  preferredPrimarySlug?: string | null;
}): string | null {
  const from = input.fromSlug.trim();
  const to = input.toSlug.trim();
  if (!from || !to || from === to) {
    return readControlBridgePrimaryWorkspace();
  }

  const preferred = input.preferredPrimarySlug?.trim() || null;
  const primary = preferred || from;
  if (to === primary) {
    writeControlBridgePrimaryWorkspace(null);
    return null;
  }

  writeControlBridgePrimaryWorkspace(primary);
  return primary;
}

/** Resolve which slug is the "primary / company home" for the current CB session. */
export function resolveControlBridgePrimaryWorkspace(input: {
  currentWorkspaceSlug: string;
  companyKryspaceSlug?: string | null;
  sessionPrimarySlug?: string | null;
}): string | null {
  const current = input.currentWorkspaceSlug.trim();
  const company = input.companyKryspaceSlug?.trim() || null;
  const session = input.sessionPrimarySlug?.trim() || null;

  if (company && company !== current) return company;
  if (session && session !== current) return session;
  return null;
}
